import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, ApiError } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_USERS } from '@/mocks/directories'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { Capability, CreatedUser, DirectoryUser, Office, Role } from '@/types/domain'

/* User management — Story 1.3; the Settings «Пользователи» tab since S-7.
 *
 * These endpoints live in the /admin group behind `admin.users.manage` (FR-56,
 * AD-5). The client offers the tab only to a holder of that capability
 * (`features/settings/settingsTabs.ts`), but hiding is an affordance, not the
 * enforcement: reaching the URL directly answers 403.
 *
 * There is no password field anywhere in this file, in either direction. The
 * server generates a credential and returns it exactly once — at creation or
 * reset — and stores only its Argon2id encoding, so no screen can offer to
 * show an existing one.
 */

/** The request body for creating or amending an account — `userBody` in
 *  backend/internal/identity/admin_handlers.go. */
export interface UserPayload {
  name: string
  email: string
  role: Role
  office: Office
  language: 'en' | 'ru'
  /** The account's OWN additive grants, as dotted keys — the whole set, not
   *  only the ones the form shows. The server replaces the stored set with
   *  this list, so a key left out is a grant revoked. Until S-7 the client sent
   *  a `grants` object the server never read: an absent `capabilities` was an
   *  empty list, and every save wiped the account's own grants. */
  capabilities: Capability[]
}

/** Reads an account off the wire with its two capability lists always lists.
 *
 *  A server older than the fix sent `userCapabilities: null` for an account
 *  with no grants of its own — most accounts — and the users table reads
 *  `.length` on every row, so one null took the whole tab into the error
 *  boundary (the S-7 browser check). */
export function toDirectoryUser(wire: DirectoryUser): DirectoryUser {
  return {
    ...wire,
    capabilities: wire.capabilities ?? [],
    userCapabilities: wire.userCapabilities ?? [],
  }
}

export function useUsers() {
  return useQuery<DirectoryUser[]>({
    queryKey: queryKeys.settings.users(),
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_USERS
        : (await api.get<{ items: DirectoryUser[] }>('/admin/users')).items.map(toDirectoryUser),
  })
}

/** Invalidates the user list after any change.
 *
 * The session is invalidated alongside it because an admin editing their
 * own record changes their own language or grants, and the header reads those
 * from the session query rather than from this list. */
function useInvalidateUsers() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.settings.users() })
    void queryClient.invalidateQueries({ queryKey: queryKeys.session })
  }
}

/** Creates an account. Resolves with the generated credential, which the caller
 *  must show once and cannot retrieve again. */
export function useCreateUser() {
  const invalidate = useInvalidateUsers()

  return useMutation<CreatedUser, Error, UserPayload>({
    mutationFn: (payload) => api.post<CreatedUser>('/admin/users', payload),
    onSuccess: invalidate,
  })
}

/** Amends an account.
 *
 * The whole record is submitted rather than a patch: user management is a form,
 * and a partial body would make "clear this grant" indistinguishable from
 * "leave it alone" — the bug that silently re-grants a permission somebody
 * just revoked. Email is absent because an address identifies an account. */
export function useUpdateUser() {
  const invalidate = useInvalidateUsers()

  return useMutation<DirectoryUser, Error, { id: string; payload: Omit<UserPayload, 'email'> }>({
    mutationFn: ({ id, payload }) => api.put<DirectoryUser>(`/admin/users/${id}`, payload),
    onSuccess: invalidate,
  })
}

/** Activates or deactivates an account.
 *
 * Deactivation ends every session that account holds server-side, so their next
 * request is refused rather than waiting for a token to expire (FR-57). */
export function useSetUserActive() {
  const invalidate = useInvalidateUsers()

  return useMutation<DirectoryUser, Error, { id: string; isActive: boolean }>({
    mutationFn: ({ id, isActive }) =>
      api.patch<DirectoryUser>(`/admin/users/${id}/active`, { isActive }),
    onSuccess: invalidate,
  })
}

/** Issues a new credential and invalidates every session the account holds. */
export function useResetUserPassword() {
  const invalidate = useInvalidateUsers()

  return useMutation<CreatedUser, Error, string>({
    mutationFn: (id) => api.post<CreatedUser>(`/admin/users/${id}/password`),
    onSuccess: invalidate,
  })
}

/* The account writes' 409 refusals, recognised by the token the server puts
 * in the problem detail (identity/admin_handlers.go `fail`, the http-api.md
 * Conflict contract). Until S-13 these were English sentences in `detail`, and
 * the screen showed the generic conflict sentence with no reason. Predicates
 * here, beside the hooks that meet them; `userRefusalMessage` words them and
 * the token never reaches the screen. */

function conflictToken(error: unknown, token: string): boolean {
  return error instanceof ApiError && error.code === 'conflict' && error.problem.detail === token
}

/** Another account already has this email (a create). */
export function userEmailTaken(error: unknown): boolean {
  return conflictToken(error, 'email-taken')
}

/** An admin tried to change their own role (an update). */
export function userSelfDemotion(error: unknown): boolean {
  return conflictToken(error, 'self-demotion')
}

/** An admin tried to deactivate their own account. */
export function userSelfDeactivation(error: unknown): boolean {
  return conflictToken(error, 'self-deactivation')
}

/** The change would leave no active super-admin — an update taking the role
 *  away, or a deactivation. */
export function userLastSuperAdmin(error: unknown): boolean {
  return conflictToken(error, 'last-super-admin')
}
